/**
 * Hotel Details Thunks
 *
 * Async thunks for hotel details operations.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { HotelDetailsTemplateData, SimilarProperties, WeaverResponse } from '@contracts';
import {
  fetchHotelDetails as fetchHotelDetailsApi,
  HotelDetailsError,
  type HotelDetailsRequestParams,
} from '../api/detailsApi';
import { setLoading } from '@/features/chat/redux';
import type { AppDispatch, RootState } from '@/store/store';
import { selectListingSearchContext } from '../selectors/hotelConfigSelectors';
import { logger } from '@/shared/utils/logger';

// ============================================
// Types
// ============================================

/**
 * Minimal params needed to fetch hotel details
 * Other params are pulled from Redux store (listingSearchContext)
 */
export interface FetchHotelDetailsParams {
  hotelId: string;
  hotelName: string;
  city?: string;
  cityCode?: string;
}

/**
 * Parameters for fetching static details (with UUIDs)
 */
export interface FetchHotelStaticDetailsParams {
  hotelId: string;
  hotelName: string;
  city?: string;
  cityCode?: string;
  uuids: string[];
}

/**
 * Response from static details fetch (partial update)
 */
export interface StaticDetailsResponse {
  similarProperties: SimilarProperties | undefined;
  weaverResponse: WeaverResponse | undefined;
  incompletedUUIDs: string[] | undefined;
}

// ============================================
// Thunks
// ============================================

/**
 * Fetch hotel details with context from listing search context.
 *
 * This thunk pulls search context data (dates, guests, currency, etc.)
 * from the listingSearchContext (from listing API response) and combines
 * it with the provided params.
 *
 * Note: The details API response will contain its own searchContext which
 * should be stored in detailsSearchContext for use in subsequent operations.
 */
export const fetchHotelDetailsThunk = createAsyncThunk<
  HotelDetailsTemplateData,
  FetchHotelDetailsParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>('hotels/fetchDetails', async (params, { getState, dispatch, rejectWithValue }) => {
  try {
    // Set loading state
    dispatch(setLoading(true));

    const state = getState();
    const searchContext = state.hotels.searchContext.searchContext;
    const listingSearchContext = selectListingSearchContext(state);

    // Extract dates from listing search context with fallback to structured searchContext
    const dates = listingSearchContext?.['dates'] as
      | { checkin?: string; checkout?: string }
      | undefined;
    const checkIn = dates?.checkin ?? searchContext.checkIn;
    const checkOut = dates?.checkout ?? searchContext.checkOut;

    // Extract paxCount from listing search context with fallback to structured searchContext
    // paxCount structure: { adults: number, rooms: number, children: number }
    const paxCount = listingSearchContext?.['paxCount'] as
      | { adults?: number; rooms?: number; children?: number }
      | undefined;
    const adultCount = paxCount?.adults ?? searchContext.roomStayCandidates.adultCount;
    const roomCount = paxCount?.rooms ?? searchContext.roomStayCandidates.rooms;
    const childCount = paxCount?.children ?? searchContext.roomStayCandidates.childAges.length;

    // Extract city and cityCode from listing search context location
    // Prioritize cityCode over locusId as locusId may be a display name (e.g., "Goa")
    // while cityCode is the actual code (e.g., "CTGOI")
    const location = listingSearchContext?.['location'] as
      | { cityCode?: string; city?: string; locusId?: string }
      | undefined;
    const city = params.city ?? location?.city;
    const cityCode = params.cityCode ?? location?.cityCode ?? location?.locusId;

    // Build complete request params using listing search context
    const requestParams: HotelDetailsRequestParams = {
      hotelId: params.hotelId,
      hotelName: params.hotelName,
      ...(city && { city }),
      ...(cityCode && { cityCode }),
      // Use dates from listing search context if available
      ...(checkIn && { checkin: checkIn }),
      ...(checkOut && { checkout: checkOut }),
      // Use currency from structured searchContext
      currency: searchContext.currency,
      // Use pax count from listing search context with fallback to structured searchContext
      adultCount,
      roomCount,
      childCount,
      // Only include childrenAges if there are children
      ...(searchContext.roomStayCandidates.childAges.length > 0 && {
        childrenAges: searchContext.roomStayCandidates.childAges,
      }),
    };

    const template = await fetchHotelDetailsApi(requestParams);

    if (!template?.data) {
      throw new HotelDetailsError('Invalid response: Template data is missing', 500, template);
    }

    // Clear loading state on success
    dispatch(setLoading(false));

    return template.data;
  } catch (error) {
    // Clear loading state on error
    dispatch(setLoading(false));

    if (error instanceof HotelDetailsError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch hotel details');
  }
});

/**
 * Legacy fetch hotel details (for backward compatibility)
 * Accepts full HotelDetailsRequestParams without pulling from store
 *
 * @deprecated Use fetchHotelDetailsThunk instead, which automatically pulls context from store
 */
export const fetchHotelDetailsWithParamsThunk = createAsyncThunk<
  HotelDetailsTemplateData,
  HotelDetailsRequestParams,
  { dispatch: AppDispatch; rejectValue: string }
>('hotels/fetchDetailsWithParams', async (params, { dispatch, rejectWithValue }) => {
  try {
    // Set loading state
    dispatch(setLoading(true));

    const template = await fetchHotelDetailsApi(params);

    if (!template?.data) {
      throw new HotelDetailsError('Invalid response: Template data is missing', 500, template);
    }

    // Clear loading state on success
    dispatch(setLoading(false));

    return template.data;
  } catch (error) {
    // Clear loading state on error
    dispatch(setLoading(false));

    if (error instanceof HotelDetailsError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch hotel details');
  }
});

/**
 * Fetch hotel static details with UUIDs for weaverResponse and similarProperties
 *
 * This thunk is called after the initial hotel details fetch when incompletedUUIDs is present.
 * It fetches additional data (weaverResponse, hotelCompareResponse) and merges into the state.
 */
export const fetchHotelStaticDetailsThunk = createAsyncThunk<
  StaticDetailsResponse,
  FetchHotelStaticDetailsParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>('hotels/fetchStaticDetails', async (params, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const searchContext = state.hotels.searchContext.searchContext;
    const listingSearchContext = selectListingSearchContext(state);

    // Extract dates from listing search context with fallback to searchContext
    const dates = listingSearchContext?.['dates'] as
      | { checkin?: string; checkout?: string }
      | undefined;
    const checkIn = dates?.checkin ?? searchContext.checkIn ?? undefined;
    const checkOut = dates?.checkout ?? searchContext.checkOut ?? undefined;

    // Extract paxCount from listing search context
    const paxCount = listingSearchContext?.['paxCount'] as
      | { adults?: number; rooms?: number; children?: number }
      | undefined;
    const adultCount = paxCount?.adults ?? searchContext.roomStayCandidates.adultCount;
    const roomCount = paxCount?.rooms ?? searchContext.roomStayCandidates.rooms;
    const childCount = paxCount?.children ?? searchContext.roomStayCandidates.childAges.length;

    // Build request params with UUIDs
    // Note: checkin/checkout will use defaults in detailsApi if not provided
    const requestParams: HotelDetailsRequestParams = {
      hotelId: params.hotelId,
      hotelName: params.hotelName,
      ...(params.city && { city: params.city }),
      ...(params.cityCode && { cityCode: params.cityCode }),
      ...(checkIn && { checkin: checkIn }),
      ...(checkOut && { checkout: checkOut }),
      currency: searchContext.currency,
      adultCount,
      roomCount,
      childCount,
      ...(searchContext.roomStayCandidates.childAges.length > 0 && {
        childrenAges: searchContext.roomStayCandidates.childAges,
      }),
      // Pass UUIDs for the HOTEL_STATIC_DETAIL API call
      uuids: params.uuids,
    };

    logger.info('[fetchHotelStaticDetailsThunk] Fetching static details:', {
      hotelId: params.hotelId,
      uuids: params.uuids,
    });

    const template = await fetchHotelDetailsApi(requestParams);

    if (!template?.data) {
      throw new HotelDetailsError('Invalid response: Template data is missing', 500, template);
    }

    logger.info('[fetchHotelStaticDetailsThunk] Received response:', {
      hasSimilarProperties: !!template.data.similarProperties,
      hasWeaverResponse: !!template.data.weaverResponse,
      incompletedUUIDs: template.data.incompletedUUIDs,
    });

    // Return only the fields we care about for static details update
    return {
      similarProperties: template.data.similarProperties,
      weaverResponse: template.data.weaverResponse,
      incompletedUUIDs: template.data.incompletedUUIDs,
    };
  } catch (error) {
    logger.error('[fetchHotelStaticDetailsThunk] Error:', error);
    if (error instanceof HotelDetailsError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch hotel static details');
  }
});
